import { UploadMonitor } from '../../file-upload/upload-monitor';
import { MessageTypeEnum } from '../enums/message-type.enum';
import { Button } from './button.model';
import { Carousel } from './carousel.model';
import { InMessageFile } from './in-message-file.model';
import { Media } from './media.model';
import { PostBack } from './postback.model';
import { StartConversation } from './start-conversation.model';
import { TextInputResponse } from './text-input-response.model';
import { TextInput } from './text-input.model';
import { Text } from './text.model';

// Note that messages parsed from the websocket are not really instances of this class
// (just js objects that conform to the structure) and hence don't have the 'new Date()' datetime
export class Message {
  id: number;
  is_from_brand = false;
  datetime = new Date().toISOString();
  type: MessageTypeEnum;
  text?: Text;
  image?: Media;
  file?: InMessageFile;
  button?: Button; // set when the user has clicked a button
  text_input?: TextInput; // special, not-displayed message which changes the type of the <input>
  text_input_response?: TextInputResponse;
  monitor?: UploadMonitor;
  buttons: Button[] = []; // set by the backend so that a set of buttons get displayed
  postFailed?: boolean; // an http error occurred while posting this message
  last_seen_message_id: number; // Last seen message from all messages except "seen_conversation" messages
  disable_input: boolean;
  buttonAlreadyClicked = false; // set by the UI, to prevent multiple clics on a message's buttons
  postback?: PostBack;
  carousel?: Carousel;

  // Computed on the fly by the service.
  dateLabel?: string;

  // it would have been better to have snake case here to be consistent, but documentation has already been sent to client
  'start-conversation'?: StartConversation;

  static newTextMessage(content: string): Message {
    const message = new Message();
    message.type = MessageTypeEnum.TEXT;
    message.text = new Text(content);
    return message;
  }

  static newImageMessage(mediaUUID: string): Message {
    const message = new Message();
    message.type = MessageTypeEnum.IMAGE;
    message.image = new Media(mediaUUID);
    return message;
  }

  static newFileMessage(fileUUID: string): Message {
    const message = new Message();
    message.type = MessageTypeEnum.FILE;
    message.file = new InMessageFile(fileUUID);
    return message;
  }

  static newButtonMessage(button: Button): Message {
    const message = new Message();
    message.type = MessageTypeEnum.BUTTON;
    message.button = button;
    return message;
  }

  static newUploadMonitorMessage(monitor: UploadMonitor): Message {
    const message = new Message();
    message.type = MessageTypeEnum.PROGRESS_BAR;
    message.monitor = monitor;
    return message;
  }

  static newTextInputResponseMessage(textInput, content) {
    const message = new Message();
    message.type = MessageTypeEnum.TEXT_INPUT_RESPONSE;
    message.text_input_response = new TextInputResponse(textInput, content);
    return message;
  }

  static newStartConversationMessage(ref?: string): Message {
    const message = new Message();
    message.type = MessageTypeEnum.START_CONVERSATION;
    message['start-conversation'] = new StartConversation(ref);
    return message;
  }

  static newPostbackMessage(title, payload): Message {
    const message = new Message();
    message.type = MessageTypeEnum.POSTBACK;
    message.postback = new PostBack(title, payload);
    return message;
  }

  static newSeenConversationMessage(last_seen_message_id: number): Message {
    const message = new Message();
    message.last_seen_message_id = last_seen_message_id;
    return message;
  }

  toBackendBody(): any {
    // Clean the body before sending it to the backend
    let body: any;
    if (this.isText()) {
      body = {
        [MessageTypeEnum.TEXT]: {
          content: this[MessageTypeEnum.TEXT].content,
        },
      };
    } else if (this.isImage()) {
      body = {
        [MessageTypeEnum.IMAGE]: {
          media_uuid: this[MessageTypeEnum.IMAGE].media_uuid,
        },
      };
    } else if (this.isFile()) {
      body = {
        [MessageTypeEnum.FILE]: {
          uuid: this[MessageTypeEnum.FILE].uuid,
        },
      };
    } else if (this.isButton()) {
      body = {
        [MessageTypeEnum.BUTTON]: {
          name: this[MessageTypeEnum.BUTTON].name,
          payload: this[MessageTypeEnum.BUTTON].payload,
        },
      };
    } else if (this.isTextInputResponse()) {
      body = {
        [MessageTypeEnum.TEXT_INPUT_RESPONSE]: {
          content: this[MessageTypeEnum.TEXT_INPUT_RESPONSE].content,
          type: this[MessageTypeEnum.TEXT_INPUT_RESPONSE].type,
          payload: this[MessageTypeEnum.TEXT_INPUT_RESPONSE].payload,
        },
      };
    } else if (this.isStartConversation()) {
      body = {
        [MessageTypeEnum.START_CONVERSATION]: {
          ref: this[MessageTypeEnum.START_CONVERSATION].ref,
        },
      };
    } else if (this.isPostback()) {
      body = {
        [MessageTypeEnum.POSTBACK]: {
          title: this[MessageTypeEnum.POSTBACK].title,
          payload: this[MessageTypeEnum.POSTBACK].payload,
        },
      };
    }
    body.type = this.type;

    return body;
  }

  // Be careful not to try and call these methods on message received from the backend:
  // as they are not created with a constructor, they won't have any methods of this class
  isText(): boolean {
    return this.type === MessageTypeEnum.TEXT;
  }
  isImage(): boolean {
    return this.type === MessageTypeEnum.IMAGE;
  }
  isFile(): boolean {
    return this.type === MessageTypeEnum.FILE;
  }
  isButton(): boolean {
    return this.type === MessageTypeEnum.BUTTON;
  }
  isProgressBar(): boolean {
    return this.type === MessageTypeEnum.PROGRESS_BAR;
  }
  isStartConversation(): boolean {
    return this.type === MessageTypeEnum.START_CONVERSATION;
  }
  isPostback(): boolean {
    return this.type === MessageTypeEnum.POSTBACK;
  }
  isReStartConversation(): boolean {
    return this.type === MessageTypeEnum.RESTART_CONVERSATION;
  }
  isTextInputResponse(): boolean {
    return this.type === MessageTypeEnum.TEXT_INPUT_RESPONSE;
  }
}
